import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DesignHuddleService } from '@app/shared/services/design-huddle/design-huddle.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { DesignHuddleTemplate, CreateDesignHuddleProjectRequest } from '@app/shared/models/design-huddle';
import { DesignHuddleTemplateSelectorComponent } from '../design-huddle-template-selector/design-huddle-template-selector.component';
import { environment } from '@env/environment';

/**
 * Design Huddle Product Creator Component
 * Wrapper component to create products using Design Huddle
 */
@Component({
  selector: 'app-design-huddle-product-creator',
  templateUrl: './design-huddle-product-creator.component.html',
  styleUrls: ['./design-huddle-product-creator.component.scss']
})
export class DesignHuddleProductCreatorComponent implements OnInit {
  @Input() userId: number;
  @Input() productId: number;
  @Input() styleId: number;
  @Input() createdBy: number;
  @Input() productName: string = 'New Design';
  @Output() onProjectCreated = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  isDesignHuddleEnabled: boolean = false;
  selectedTemplate: DesignHuddleTemplate | null = null;
  projectId: number | null = null;
  editorUrl: string | null = null;
  showEditor: boolean = false;

  constructor(
    private dialog: MatDialog,
    private designHuddleService: DesignHuddleService,
    private notificationService: NotificationService,
    private loader: LoaderService
  ) { }

  ngOnInit(): void {
    this.isDesignHuddleEnabled = environment.designHuddleEnabled || false;
  }

  /**
   * Open template selector dialog
   */
  openTemplateSelector(): void {
    const dialogRef = this.dialog.open(DesignHuddleTemplateSelectorComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: false
    });

    dialogRef.componentInstance.onTemplateSelected.subscribe((template: DesignHuddleTemplate) => {
      this.selectedTemplate = template;
      dialogRef.close();
      this.createProject();
    });

    dialogRef.componentInstance.onCancel.subscribe(() => {
      dialogRef.close();
    });
  }

  /**
   * Create Design Huddle project
   */
  private createProject(): void {
    if (!this.selectedTemplate || !this.userId) {
      this.notificationService.error('Template and user are required', 'Error');
      return;
    }

    this.loader.show();

    const request: CreateDesignHuddleProjectRequest = {
      userId: this.userId,
      projectName: this.productName || `Design - ${new Date().toLocaleDateString()}`,
      templateId: this.selectedTemplate.templateId,
      description: `Created from template: ${this.selectedTemplate.name}`,
      productId: this.productId,
      styleId: this.styleId,
      createdBy: this.createdBy || this.userId,
      customFields: {
        category: this.selectedTemplate.category,
        createdBy: 'Angular App'
      }
    };

    this.designHuddleService.createProject(request).subscribe({
      next: (response) => {
        this.loader.hide();

        if (response.StatusCode === 200 && response.Data?.ResponseData) {
          const data = response.Data.ResponseData;

          if (data.success) {
            this.projectId = data.projectId;
            this.editorUrl = data.editorUrl;
            this.showEditor = true;

            this.notificationService.success('Project created successfully', 'Success');

            // Emit project creation event
            this.onProjectCreated.emit({
              projectId: this.projectId,
              editorUrl: this.editorUrl,
              customizedProductId: data.customizedProductId,
              templateId: this.selectedTemplate.templateId
            });
          } else {
            this.notificationService.error(
              data.errorMessage || 'Failed to create project',
              'Error'
            );
          }
        } else {
          this.notificationService.error('Failed to create project', 'Error');
        }
      },
      error: (error) => {
        this.loader.hide();
        this.notificationService.error('Failed to create project', 'Error');
        console.error('Create project error:', error);
      }
    });
  }

  /**
   * Handle editor save event
   */
  onEditorSave(event: { projectId: number }): void {
    console.log('Editor saved:', event);
    this.notificationService.success('Design saved', 'Success');
  }

  /**
   * Handle editor publish event
   */
  onEditorPublish(event: { projectId: number }): void {
    console.log('Editor published:', event);
    this.notificationService.success('Design published', 'Success');
  }

  /**
   * Handle editor close event
   */
  onEditorClose(): void {
    this.showEditor = false;
    this.onCancel.emit();
  }

  /**
   * Handle editor error
   */
  onEditorError(event: { error: string }): void {
    this.notificationService.error(event.error, 'Editor Error');
  }

  /**
   * Cancel creation
   */
  cancel(): void {
    this.onCancel.emit();
  }
}
